// src/components/calendar/RequestSummary.jsx
import React, { useMemo } from 'react';
import { addMinutes } from '../../contexts/BookingContext';

// ─── Constants / helpers ──────────────────────────────────────────────────────

const LESSON_MINUTES = 50;

const safeNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const toLocalDate = (ymd) => {
  if (typeof ymd !== 'string') return null;
  const [Y, M, D] = ymd.split('-').map(Number);
  return Y && M && D ? new Date(Y, M - 1, D, 12) : null;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const SummaryRow = ({ label, value }) => (
  <div className="flex items-center justify-between pb-2.5 border-b border-gray-100 last:border-0 last:pb-0">
    <span className="text-xs sm:text-sm text-gray-500">{label}</span>
    <span className="text-xs sm:text-sm font-medium text-gray-800">{value}</span>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

const RequestSummary = ({ course, selectedDate, selectedTimes, handleSubmit }) => {
  const unitPrice = safeNumber(course?.price, 0);

  const range = useMemo(() => {
    if (!selectedTimes?.length) return null;
    const sorted  = [...selectedTimes].sort();
    const count   = sorted.length;
    const endTime = addMinutes(sorted[0], count * LESSON_MINUTES);
    return { start: sorted[0], endTime, count };
  }, [selectedTimes]);

  const total       = useMemo(() => (range ? unitPrice * range.count : 0), [unitPrice, range]);
  const displayDate = useMemo(() => {
    if (!selectedDate) return '';
    const d = toLocalDate(selectedDate);
    return d ? d.toLocaleDateString('he-IL') : selectedDate;
  }, [selectedDate]);

  const canSubmit = (selectedTimes?.length ?? 0) > 0 && unitPrice > 0;

  return (
    <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 sm:p-5 sticky top-4">
      <h2 className="text-base sm:text-lg font-bold text-gray-800 mb-4 pb-3 border-b border-gray-100">
        סיכום הזמנה
      </h2>

      <div className="space-y-2.5">
        <SummaryRow label="מחיר לשיעור (50 דק׳)" value={`₪${unitPrice.toFixed(0)}`} />
        <SummaryRow label="מספר שיעורים"          value={range?.count ?? 0} />
        {selectedDate && <SummaryRow label="תאריך" value={displayDate} />}
        {range && (
          <SummaryRow
            label="זמן"
            value={
              <span dir="ltr" className="tabular-nums">
                {range.start} – {range.endTime}
              </span>
            }
          />
        )}
      </div>

      {/* Total */}
      <div className="flex items-baseline justify-between mt-4 pt-3 border-t border-gray-100">
        <span className="font-bold text-sm sm:text-base text-gray-800">סך הכל:</span>
        <span className="font-bold text-base sm:text-lg text-indigo-600">₪{total.toFixed(0)}</span>
      </div>
      <p className="text-xs text-gray-400 mt-0.5">*המחיר כולל מע"מ</p>

      {/* CTA */}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className={`w-full mt-4 sm:mt-5 py-2.5 sm:py-3 rounded-xl text-sm sm:text-base font-semibold transition-all ${
          canSubmit
            ? 'bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-md'
            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
      >
        שלח בקשה
      </button>

      {/* Notice */}
      
      <div className="mt-3 sm:mt-4 relative overflow-hidden rounded-xl border border-amber-200 bg-gradient-to-l from-amber-50 to-orange-50">
        {/* RTL accent bar */}
        <div className="absolute right-0 top-0 h-full w-1 rounded-r-xl bg-gradient-to-b from-amber-400 to-orange-400" />

        <div className="flex items-start gap-3 px-4 py-3 pr-5">
          
          {/* Text */}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-amber-800 mb-0.5">⚠️ שים לב!</p>
            <p className="text-xs text-amber-700 leading-5">
              לאחר אישור המורה, יש להשלים את התשלום בתוך{" "}
              <span className="inline-flex items-center gap-0.5 font-semibold text-amber-900 bg-amber-100 px-1.5 py-0.5 rounded-md">
                5 שעות
              </span>{" "}
              ממועד האישור, ובכל מקרה לא יאוחר מ־{" "}
              <span className="inline-flex items-center gap-0.5 font-semibold text-amber-900 bg-amber-100 px-1.5 py-0.5 rounded-md">
                שעה לפני תחילת השיעור
              </span>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestSummary;